import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ServicesPage.css';
import { servicesAPI, cartAPI } from '../../services/api';
import { isAuthenticated } from '../../services/api';
import { useToast } from '../../Components/Toast/Toast';

const ServicesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [services, setServices] = useState([]);
  console.log(services)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cartItems, setCartItems] = useState(0);
  const [downloadingSlug, setDownloadingSlug] = useState(null);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const response = await servicesAPI.getAllServices(params);
      if (response.success) {
        setServices(response.data?.services || []);
      } else {
        setError(response.message || 'Failed to fetch services');
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await servicesAPI.getServiceCategories();
      if (response.success) {
        setCategories(response.data?.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      const response = await cartAPI.getCart();
      if (response.success) {
        setCartItems(response.data?.cart?.items?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, []);

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchServices();
    fetchCategories();
    if (isAuthenticated()) {
      fetchCart();
    }
  }, [fetchServices, fetchCategories, fetchCart]);

  const handleAddToCart = async (serviceId) => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      console.log('Adding service to cart:', serviceId); // Debug log
      const response = await cartAPI.addToCart(serviceId);
      console.log('Add to cart response:', response); // Debug log

      if (response.success) {
        setCartItems(prev => prev + 1);
        // Show success message and redirect to profile cart
        toast.success('Service added to cart successfully!');
        navigate('/myProfile?tab=cart');
      } else {
        toast.error(response.message || 'Failed to add service to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add service to cart');
    }
  };

  const handleViewDetails = (service) => {
    navigate(`/service/${service.slug}`, { state: { service } });
  };

  const handleDownloadTemplate = async (e, service) => {
    e.stopPropagation();
    if (!isAuthenticated()) {
      toast.info('Please login to download the template');
      navigate('/login');
      return;
    }
    try {
      setDownloadingSlug(service.slug);
      const blob = await servicesAPI.downloadRawTemplate(service.slug);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${service.name.replace(/\s+/g, '_')}_Template.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(error.message || 'Failed to download template');
    } finally {
      setDownloadingSlug(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchServices();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    fetchServices();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getProcessingTypeColor = (type) => {
    switch (type) {
      case 'automatic': return '#10B981'; // Green
      case 'manual': return '#F59E0B'; // Yellow
      case 'hybrid': return '#3B82F6'; // Blue
      default: return '#6B7280'; // Gray
    }
  };

  const getProcessingTypeText = (type) => {
    switch (type) {
      case 'automatic': return 'AI Processing';
      case 'manual': return 'Expert Review';
      case 'hybrid': return 'AI + Expert';
      default: return 'Processing';
    }
  };

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (loading) {
    return (
      <div className="services-page">
        <div className="services-loading">
          <div className="loading-spinner"></div>
          <p>Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="services-page">
      <div className="services-container">
        {/* Header */}
        <div className="services-header">
          <h1>Our Services</h1>
          <p>Professional document processing and legal services</p>

          {/* Cart Icon */}
          {isAuthenticated() && (
            <div className="cart-icon" onClick={() => navigate('/myProfile?tab=cart')}>
              <span className="cart-count">{cartItems}</span>
              🛒
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="services-filters">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              🔍
            </button>
          </form>

          <div className="category-filters">
            <button
              className={`category-btn ${selectedCategory === '' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('')}
            >
              All Services
            </button>
            {categories.map((category) => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category)}
              >
                {category.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Services Grid */}
        <div className="services-grid">
          {services.map((service) => (
            <div key={service._id} className="service-card">
              <div className="service-header">
                <h3 className="service-title">{service.name}</h3>
                <div
                  className="processing-type"
                  style={{ backgroundColor: getProcessingTypeColor(service.processingType) }}
                >
                  {getProcessingTypeText(service.processingType)}
                </div>
              </div>

              <div className="service-content">
                <p className="service-description">{service.description}</p>

                <div className="service-details">
                  <div className="service-price">
                    {formatPrice(service.price)}
                  </div>
                  <div className="service-time">
                    ⏱️ {service.estimatedTime}
                  </div>
                </div>

                <div className="service-requirements">
                  <h4>Required Documents:</h4>
                  <ul>
                    {(service.requiredDocuments || []).slice(0, 3).map((doc, index) => (
                      <li key={index}>
                        {doc.name} {doc.isRequired ? '*' : '(Optional)'}
                        <button
                          className="doc-download-btn"
                          onClick={(e) => handleDownloadTemplate(e, service)}
                          disabled={downloadingSlug === service.slug}
                          data-tooltip="Download raw template"
                        >
                          {downloadingSlug === service.slug ? '⏳' : '📥'}
                        </button>
                      </li>
                    ))}
                    {(service.requiredDocuments || []).length > 3 && (
                      <li>+{service.requiredDocuments.length - 3} more</li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="service-actions">
                <button
                  className="btn-secondary"
                  onClick={() => handleViewDetails(service)}
                >
                  View Details
                </button>
                <button
                  className="btn-primary"
                  onClick={() => handleAddToCart(service._id)}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Services Message */}
        {services.length === 0 && !loading && (
          <div className="no-services">
            <h3>No services found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;
